import React from 'react';
import { Card, Row, Col, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
    Calendar,
    FileText,
    Upload,
    CheckCircle,
    Clock,
    XCircle,
    Edit,
    Trash2,
    Eye,
    ArrowRight,
    BookOpen,
    Home,
    Users,
    Star,
    MessageSquare,
    Phone,
    Mail
} from 'lucide-react';
import { useUserData } from '../../hooks';

const Dashboard = () => {
    const navigate = useNavigate();
    const { name, role } = useUserData();

    return (
        <div>
            {/* Welcome Header */}
            <div className="mb-4">
                <h1 className="fw-bold mb-2" style={{ fontSize: '2rem', color: '#1e293b' }}>
                    Welcome back, {name || 'User'}! 👋
                </h1>
                <p className="text-muted" style={{ fontSize: '1rem' }}>
                    Here's your quick guide to managing reservations in the Pure Key Homes system.
                </p>
            </div>

            <Row className="g-4">
                {/* Left Column - Documentation */}
                <Col lg={8}>
                    {/* Getting Started Card */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <BookOpen size={24} color="#d89d37" />
                            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Getting Started</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Alert variant="info" className="mb-3">
                                <strong>💡 Quick Tip:</strong> You can create reservations in three ways - manually, by uploading a PDF invoice, or directly from the calendar views (Multi-Calendar, House Calendar). Choose the method that works best for your workflow!
                            </Alert>
                        </Card.Body>
                    </Card>

                    {/* Manual Reservation Creation */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <Edit size={24} color="#10b981" />
                            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Method 1: Create Reservation Manually</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <div className="mb-3">
                                <h6 className="fw-semibold mb-3" style={{ color: '#475569' }}>Step-by-Step Guide:</h6>
                                <ol style={{ lineHeight: '1.8', color: '#64748b' }}>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Navigate to Reservations:</strong> Click on the "Reservations" menu item in the left sidebar.
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Open the Form:</strong> Click the "New Reservation" button at the top of the page.
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Fill in Guest Details:</strong>
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li>Guest first name and last name</li>
                                            <li>Email address (for confirmation emails)</li>
                                            <li>Phone number (optional but recommended)</li>
                                        </ul>
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Select Property:</strong> Choose the house/property from the dropdown list.
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Set Dates:</strong>
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li>Check-in date (guest arrival)</li>
                                            <li>Check-out date (guest departure)</li>
                                        </ul>
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Add Booking Details:</strong>
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li>Number of guests</li>
                                            <li>Booking platform (Airbnb, Booking.com, Direct, etc.)</li>
                                            <li>Total price</li>
                                            <li>Any special notes or requests</li>
                                        </ul>
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Set Reservation Status:</strong> Choose between:
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li><span className="badge bg-warning text-dark">Pending</span> - Not yet confirmed</li>
                                            <li><span className="badge bg-success">Confirmed</span> - Booking confirmed and paid</li>
                                        </ul>
                                    </li>
                                    <li>
                                        <strong style={{ color: '#1e293b' }}>Save:</strong> Click "Create Reservation" to save the booking.
                                    </li>
                                </ol>
                            </div>

                            <Alert variant="success" className="mb-0">
                                <strong>✅ Pro Tip:</strong> Double-check dates to avoid double-bookings! The system will warn you if there's a conflict.
                            </Alert>
                        </Card.Body>
                    </Card>

                    {/* PDF Upload Method */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <Upload size={24} color="#3b82f6" />
                            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Method 2: Upload PDF Invoice</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <div className="mb-3">
                                <h6 className="fw-semibold mb-3" style={{ color: '#475569' }}>How It Works:</h6>
                                <ol style={{ lineHeight: '1.8', color: '#64748b' }}>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Go to Reservations:</strong> Navigate to the Reservations page from the sidebar.
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Find Upload Button:</strong> Look for "Upload PDF Invoice" or import button.
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Select Your PDF:</strong>
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li>Accepted formats: PDF files from booking platforms</li>
                                            <li>Supported platforms: Airbnb, Booking.com, VRBO invoices</li>
                                            <li>Maximum file size: 10MB</li>
                                        </ul>
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>AI Processing:</strong> The system will automatically extract:
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li>Guest name and contact info</li>
                                            <li>Check-in and check-out dates</li>
                                            <li>Property/house details</li>
                                            <li>Booking price and platform</li>
                                            <li>Reservation ID/code</li>
                                        </ul>
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Review & Confirm:</strong> Check the extracted data for accuracy before saving.
                                    </li>
                                    <li>
                                        <strong style={{ color: '#1e293b' }}>Save Reservation:</strong> Click confirm to add the reservation to your calendar.
                                    </li>
                                </ol>
                            </div>

                            <Alert variant="primary" className="mb-0">
                                <strong>⚡ Time Saver:</strong> PDF upload is perfect for bulk importing reservations from booking platforms!
                            </Alert>
                        </Card.Body>
                    </Card>

                    {/* Calendar Method */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <Calendar size={24} color="#8b5cf6" />
                            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Method 3: Create from Calendar Views</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <div className="mb-3">
                                <h6 className="fw-semibold mb-3" style={{ color: '#475569' }}>Quick & Visual:</h6>
                                <ol style={{ lineHeight: '1.8', color: '#64748b' }}>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Navigate to Reservations:</strong> Go to the Reservations page from the sidebar.
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Choose Calendar View:</strong> Select either:
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li><strong>Multi-Calendar View:</strong> See all properties in a monthly grid layout</li>
                                            <li><strong>House Calendar View:</strong> Timeline view by property with resizable columns</li>
                                        </ul>
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Click on a Date:</strong> Click directly on any available date slot in the calendar to start creating a reservation.
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Select Date Range:</strong>
                                        <ul className="mt-2" style={{ listStyleType: 'circle' }}>
                                            <li>First click: Select check-in date</li>
                                            <li>Second click: Select check-out date</li>
                                            <li>The system will highlight the selected range</li>
                                        </ul>
                                    </li>
                                    <li className="mb-2">
                                        <strong style={{ color: '#1e293b' }}>Fill in Details:</strong> A form will appear where you can enter guest information, booking details, and set the status.
                                    </li>
                                    <li>
                                        <strong style={{ color: '#1e293b' }}>Save:</strong> The reservation will instantly appear in the calendar view.
                                    </li>
                                </ol>
                            </div>

                            <Alert variant="success" className="mb-0">
                                <strong>👁️ Visual Advantage:</strong> Perfect for quick bookings when you can see available dates at a glance!
                            </Alert>
                        </Card.Body>
                    </Card>

                    {/* Reservation Workflow */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <CheckCircle size={24} color="#8b5cf6" />
                            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Reservation Workflow & States</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <div className="mb-4">
                                <h6 className="fw-semibold mb-3" style={{ color: '#475569' }}>Understanding Reservation States:</h6>

                                <div className="mb-3 p-3" style={{ backgroundColor: '#fff3cd', borderRadius: '8px', borderLeft: '4px solid #ffc107' }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <Clock size={18} color="#856404" />
                                        <strong style={{ color: '#856404' }}>Pending</strong>
                                    </div>
                                    <p className="mb-0" style={{ fontSize: '0.9rem', color: '#856404' }}>
                                        The reservation is created but not yet confirmed. Payment may be pending or awaiting guest confirmation.
                                    </p>
                                </div>

                                <div className="mb-3 p-3" style={{ backgroundColor: '#d4edda', borderRadius: '8px', borderLeft: '4px solid #28a745' }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <CheckCircle size={18} color="#155724" />
                                        <strong style={{ color: '#155724' }}>Confirmed</strong>
                                    </div>
                                    <p className="mb-0" style={{ fontSize: '0.9rem', color: '#155724' }}>
                                        The booking is confirmed and paid. The property is reserved for the guest on the specified dates.
                                    </p>
                                </div>

                                <div className="mb-3 p-3" style={{ backgroundColor: '#f8d7da', borderRadius: '8px', borderLeft: '4px solid #dc3545' }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <XCircle size={18} color="#721c24" />
                                        <strong style={{ color: '#721c24' }}>Cancelled</strong>
                                    </div>
                                    <p className="mb-0" style={{ fontSize: '0.9rem', color: '#721c24' }}>
                                        The reservation has been cancelled by the guest or host. The property becomes available again.
                                    </p>
                                </div>
                            </div>

                            <div className="mb-3">
                                <h6 className="fw-semibold mb-3" style={{ color: '#475569' }}>Managing Reservations:</h6>
                                <div className="table-responsive">
                                    <table className="table" style={{ fontSize: '0.9rem' }}>
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th style={{ color: '#1e293b' }}>Action</th>
                                                <th style={{ color: '#1e293b' }}>Description</th>
                                                <th style={{ color: '#1e293b' }}>Icon</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><strong>View Details</strong></td>
                                                <td>Click on any reservation to see full details, guest info, and notes</td>
                                                <td><Eye size={18} color="#64748b" /></td>
                                            </tr>
                                            <tr>
                                                <td><strong>Edit Reservation</strong></td>
                                                <td>Modify dates, guest details, price, or status of existing bookings</td>
                                                <td><Edit size={18} color="#3b82f6" /></td>
                                            </tr>
                                            <tr>
                                                <td><strong>Delete Reservation</strong></td>
                                                <td>Permanently remove a reservation (use with caution!)</td>
                                                <td><Trash2 size={18} color="#dc3545" /></td>
                                            </tr>
                                            <tr>
                                                <td><strong>Change Status</strong></td>
                                                <td>Update between Pending → Confirmed or mark as Cancelled</td>
                                                <td><CheckCircle size={18} color="#10b981" /></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Calendar Views */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <Calendar size={24} color="#f59e0b" />
                            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Calendar Views</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <p style={{ color: '#64748b', lineHeight: '1.8' }}>
                                The Reservations page offers multiple calendar views to help you visualize your bookings:
                            </p>
                            <ul style={{ lineHeight: '1.8', color: '#64748b' }}>
                                <li><strong style={{ color: '#1e293b' }}>Multi-Calendar View:</strong> See all properties side-by-side in a monthly calendar format</li>
                                <li><strong style={{ color: '#1e293b' }}>House Calendar View:</strong> View reservations by property with horizontal timeline (supports manual column resizing!)</li>
                                <li><strong style={{ color: '#1e293b' }}>Table View:</strong> List format with filters and search capabilities</li>
                                <li><strong style={{ color: '#1e293b' }}>Time Ranges:</strong> Switch between Week, Month, 2 Months, 6 Months, or Year views</li>
                            </ul>
                        </Card.Body>
                    </Card>

                    {/* Best Practices */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <Star size={24} color="#f59e0b" />
                            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Best Practices</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <ul style={{ lineHeight: '1.8', color: '#64748b' }}>
                                <li className="mb-2">✅ Always verify dates before confirming a reservation to avoid double-bookings</li>
                                <li className="mb-2">✅ Add guest contact information for emergency communications</li>
                                <li className="mb-2">✅ Include special requests or notes (early check-in, pets, etc.)</li>
                            </ul>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Column - Quick Actions & System Info */}
                <Col lg={4}>
                    {/* Quick Actions */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px',
                            position: 'sticky',
                            top: '20px'
                        }}
                    >
                        <Card.Header
                            className="bg-white"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Quick Actions</h5>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="d-grid gap-3">
                                {/* Main CTA - Go to Reservations */}
                                <Button
                                    variant="primary"
                                    size="lg"
                                    className="d-flex align-items-center justify-content-center gap-2"
                                    style={{
                                        borderRadius: '12px',
                                        padding: '16px',
                                        fontWeight: '600',
                                        fontSize: '1rem',
                                        background: 'linear-gradient(135deg, #d89d37 0%, #c48f31 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(216, 157, 55, 0.3)'
                                    }}
                                    onClick={() => navigate('/cms/reservations')}
                                >
                                    <Calendar size={20} />
                                    Go to Reservations
                                    <ArrowRight size={20} />
                                </Button>

                                {/* Admin/Super Admin Quick Actions */}
                                {(role === 'Admin' || role === 'Super Admin') && (
                                    <>
                                        <div style={{ borderTop: '1px solid #e5e7eb', margin: '8px 0' }} />

                                        <Button
                                            variant="outline-primary"
                                            className="d-flex align-items-center justify-content-start gap-3"
                                            style={{
                                                borderRadius: '10px',
                                                padding: '12px 16px',
                                                fontWeight: '500',
                                                fontSize: '0.9rem',
                                                textAlign: 'left'
                                            }}
                                            onClick={() => navigate('/cms/houses')}
                                        >
                                            <Home size={18} />
                                            <span>Manage Properties</span>
                                        </Button>

                                        <Button
                                            variant="outline-secondary"
                                            className="d-flex align-items-center justify-content-start gap-3"
                                            style={{
                                                borderRadius: '10px',
                                                padding: '12px 16px',
                                                fontWeight: '500',
                                                fontSize: '0.9rem',
                                                textAlign: 'left'
                                            }}
                                            onClick={() => navigate('/cms/reservation-history')}
                                        >
                                            <Clock size={18} />
                                            <span>View History</span>
                                        </Button>

                                        <Button
                                            variant="outline-success"
                                            className="d-flex align-items-center justify-content-start gap-3"
                                            style={{
                                                borderRadius: '10px',
                                                padding: '12px 16px',
                                                fontWeight: '500',
                                                fontSize: '0.9rem',
                                                textAlign: 'left'
                                            }}
                                            onClick={() => navigate('/cms/ical')}
                                        >
                                            <FileText size={18} />
                                            <span>iCal Sync Settings</span>
                                        </Button>

                                        <Button
                                            variant="outline-info"
                                            className="d-flex align-items-center justify-content-start gap-3"
                                            style={{
                                                borderRadius: '10px',
                                                padding: '12px 16px',
                                                fontWeight: '500',
                                                fontSize: '0.9rem',
                                                textAlign: 'left'
                                            }}
                                            onClick={() => navigate('/cms/users')}
                                        >
                                            <Users size={18} />
                                            <span>Manage Users</span>
                                        </Button>
                                    </>
                                )}
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Contact Information */}
                    <Card
                        className="border-0 mb-4"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px'
                        }}
                    >
                        <Card.Header
                            className="bg-white d-flex align-items-center gap-2"
                            style={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '20px 24px',
                                borderRadius: '16px 16px 0 0'
                            }}
                        >
                            <MessageSquare size={20} color="#1e293b" />
                            <h6 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Need Help?</h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <p style={{ fontSize: '0.9rem', color: '#64748b', lineHeight: '1.6', marginBottom: '20px' }}>
                                If you have questions or need assistance with the system, please contact support:
                            </p>
                            <div className="d-flex flex-column gap-3">
                                <a
                                    href="tel:+34614005838"
                                    className="d-flex align-items-center gap-3 text-decoration-none"
                                    style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '10px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#e9ecef';
                                        e.currentTarget.style.transform = 'translateX(4px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        e.currentTarget.style.transform = 'translateX(0)';
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#10b981',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Phone size={20} color="white" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '2px' }}>Phone</div>
                                        <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: '600' }}>+34 614 005 838</div>
                                    </div>
                                </a>

                                <a
                                    href="mailto:pedrosimoesdeveloper@gmail.com"
                                    className="d-flex align-items-center gap-3 text-decoration-none"
                                    style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '10px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#e9ecef';
                                        e.currentTarget.style.transform = 'translateX(4px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        e.currentTarget.style.transform = 'translateX(0)';
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#3b82f6',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Mail size={20} color="white" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '2px' }}>Email</div>
                                        <div style={{ fontSize: '0.85rem', color: '#1e293b', fontWeight: '600', wordBreak: 'break-word' }}>
                                            pedrosimoesdeveloper@gmail.com
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Tips Card */}
                    <Card
                        className="border-0"
                        style={{
                            boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                            borderRadius: '16px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white'
                        }}
                    >
                        <Card.Body className="p-4">
                            <h6 className="mb-3 fw-bold">💡 Did You Know?</h6>
                            <p style={{ fontSize: '0.85rem', lineHeight: '1.6', marginBottom: 0 }}>
                                You can resize the property column in the House Calendar View by dragging the handle on the right edge of the "Property" header. This works on both desktop and mobile!
                            </p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
